<template>
  <div class="relative h-full">
    <ak-basic-container class="flex flex--column">
      <ak-box narrow class="pb-none">
        <ak-button
          size="tiny"
          class="pull-right"
          tag="router-link"
          :to="allLeadsLink"
        >
          {{ leadsButtonText }}
        </ak-button>
        <h4 class="mb-none color-grey text-capitalize">
          {{ pageTitle }}
        </h4>
      </ak-box>

      <ak-box narrow class="pt-small">
        <ak-pack>
          <ak-pack-item>
            <ak-form @submit="searchApps">
              <ak-input
                v-model="searchValue"
                type="search"
                icon="search"
                :placeholder="inputPlaceholder"
                @right-icon-click="resetSearch"
              />
            </ak-form>
          </ak-pack-item>
          <ak-pack-item grow>
            <ak-button-group class="bg-white" ghost>
              <ak-button
                size="small"
                color="grey-light"
                :ghost="activePublishedStatus !== 'published'"
                @click="setStateFilter('published')"
                >Published</ak-button
              >
              <ak-button
                size="small"
                color="grey-light"
                :ghost="activePublishedStatus !== 'draft'"
                @click="setStateFilter('draft')"
                >Draft</ak-button
              >
              <ak-button
                v-if="display('hide', 'accountuser,accountadmin,reporter')"
                size="small"
                color="grey-light"
                :ghost="activePublishedStatus !== 'hidden'"
                @click="setStateFilter('hidden')"
                >Hidden</ak-button
              >
            </ak-button-group>
            <ak-tag class="ml-small" ghost badge>{{ total }} results</ak-tag>
          </ak-pack-item>

          <ak-pack-item :right="true">
            <ak-pack alignment="top">
              <ak-dropdown size="auto" aria-role="list" position-x="right">
                <template #trigger>
                  <ak-tooltip>
                    <template #trigger>
                      <ak-button
                        ghost
                        size="small"
                        color="grey-light"
                        class="bg-white ak-btn--single-icon--tiny"
                      >
                        <ak-icon type="arrow-sort" size="big" />
                      </ak-button>
                    </template>
                    Sorting
                  </ak-tooltip>
                </template>
                <template #default>
                  <ak-dropdown-item aria-role="listitem" custom>
                    <ak-field addons class="mb-none width-250">
                      <ak-select
                        v-model="computedSortProp"
                        :options="sortFieldOptions"
                        track-by="value"
                        label="label"
                      />
                      <ak-button
                        class="text-capitalize"
                        color="secondary"
                        @click="toggleSortDirection"
                      >
                        {{ sortDirection }}
                      </ak-button>
                    </ak-field>
                  </ak-dropdown-item>
                </template>
              </ak-dropdown>

              <ak-button-group
                v-if="appType === 'pages'"
                ghost
                class="ml-small bg-white"
              >
                <ak-button
                  :ghost="currentView !== 'list'"
                  size="small"
                  color="grey-light"
                  class="ak-btn--single-icon--tiny"
                  @click="setView('list')"
                  ><ak-icon type="list" size="big"
                /></ak-button>
                <ak-button
                  :ghost="currentView !== 'grid'"
                  size="small"
                  color="grey-light"
                  class="ak-btn--single-icon--tiny"
                  @click="setView('grid')"
                  ><ak-icon type="grid" size="big"
                /></ak-button>
              </ak-button-group>
            </ak-pack>
          </ak-pack-item>
        </ak-pack>
      </ak-box>

      <div class="has-fab-bottom">
        <ak-box narrow>
          <template v-if="currentView === 'list'">
            <ak-apps-list-table
              v-model="apps"
              :loading="loading"
              :app-type="appType"
              :active-account="activeAccount"
              :active-agency="activeAgency"
              :domains="domains"
              :new-app-link="newAppLink"
              :delete-app="deleteApp"
              :clone-app="cloneAppModal"
              :create-url="createUrlModal"
              :url-builder-enabled="urlBuilderEnabled"
              :current-view="currentView"
            />
          </template>

          <template v-else>
            <ak-apps-list-cards
              v-model="apps"
              :loading="loading"
              :app-type="appType"
              :active-account="activeAccount"
              :active-agency="activeAgency"
              :domains="domains"
              :new-app-link="newAppLink"
              :delete-app="deleteApp"
              :clone-app="cloneAppModal"
              :create-url="createUrlModal"
              :url-builder-enabled="urlBuilderEnabled"
              :current-view="currentView"
            />
          </template>
        </ak-box>

        <ak-box
          v-if="!loading && apps.length !== total"
          class="text-center mt-large mb"
        >
          <ak-button
            :loading-color="'white'"
            class="ml-large"
            :is-loading="appsLoading"
            @click="getMore"
          >
            More
          </ak-button>
        </ak-box>
      </div>

      <ak-fab
        class="z-index--20"
        tag="router-link"
        :to="newAppLink"
        icon="plus"
        position-x="center"
        position-y="bottom"
      />
    </ak-basic-container>
  </div>
</template>

<script>
import BasicContainer from '../../containers/BasicContainer.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import ButtonGroup from '@akerolabs/akero-vue/src/components/buttonGroup/ButtonGroup.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Pack from '@akerolabs/akero-vue/src/components/pack/Pack.vue'
import PackItem from '@akerolabs/akero-vue/src/components/pack/PackItem.vue'
import Tag from '@akerolabs/akero-vue/src/components/tag/Tag.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Dropdown from '@akerolabs/akero-vue/src/components/dropdown/Dropdown.vue'
import DropdownItem from '@akerolabs/akero-vue/src/components/dropdown/DropdownItem.vue'
import Fab from '@akerolabs/akero-vue/src/components/fab/Fab.vue'
import Tooltip from '@akerolabs/akero-vue/src/components/tooltip/Tooltip.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import { DialogProgrammatic } from '@akerolabs/akero-vue/src/components/dialog'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'
import { ModalProgrammatic } from '@akerolabs/akero-vue/src/components/modal/index.js'

import AppsListTable from './AppsListTable.vue'
import AppsListCards from './AppsListCards.vue'
import CloneMoveModal from './CloneMoveModal.vue'
import AppUrlBuilder from '../../urlBuilder/AppsUrlBuilder/Builder.vue'

import {
  capitalizeFirstLetter,
  debounce,
  mapStats,
  setIntervalX,
  cloneObject,
} from '@akerolabs/akero-vue/src/utils/helpers.js'

import { request } from '@akerolabs/akero-vue/src/utils/request'
import { defineComponent, ref, computed, onMounted } from 'vue'

import { entityBranding } from '@akerolabs/akero-vue/src/services/entityBranding.js'
import { isAllowed } from '@akerolabs/akero-vue/src/services/features.js'
import { userObj, display } from '@akerolabs/akero-vue/src/services/users.js'
import { useRouter } from '../../../legacy/router'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-apps-list />
   */
  name: 'AkAppsList',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [BasicContainer.name]: BasicContainer,
    [ButtonGroup.name]: ButtonGroup,
    [Button.name]: Button,
    [Form.name]: Form,
    [Input.name]: Input,
    [Box.name]: Box,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [Tag.name]: Tag,
    [Icon.name]: Icon,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [Fab.name]: Fab,
    [Tooltip.name]: Tooltip,
    [Select.name]: Select,
    [AppsListTable.name]: AppsListTable,
    [AppsListCards.name]: AppsListCards,
    [Field.name]: Field,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @description The active account (drilled in or users)
     */
    activeAccount: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The active agency (drilled in or users)
     */
    activeAgency: {
      default: () => ({}),
      type: Object,
    },

    appType: {
      default: 'pages',
      type: String,
    },

    setState: {
      default: () => ({}),
      type: Function,
    },

    getState: {
      default: () => ({}),
      type: Function,
    },

    onCampaignChange: {
      default: () => ({}),
      type: Function,
    },

    /**
     * @type {String}
     * @description the active campaign id
     */
    campaignId: {
      default: undefined,
      type: [String, undefined],
    },

    /**
     * @type {Object}
     * @description This object is the store for the campaign sidebar in angular,
     * once the campaignsidebar is converted we will remove this property
     * and import the store.js from src/components/folders/campaignSidebar
     */
    campaignSidebarStore: {
      default: () => ({}),
      type: Object,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const router = useRouter()

    const levels = ['reporter', 'accountadmin', 'accountuser']
    const protocol = location.protocol + '//'
    const statProp = ref('')
    const searchValue = ref('')
    const currentView = ref('list')
    const activePublishedStatus = ref('')
    const apps = ref([])
    const total = ref(0)
    const currentPage = ref(1)
    const sortDirection = ref('desc')
    const sortProp = ref({ value: 'createdOn', label: 'Created Date' })
    const loading = ref(true)
    const paging = ref({})
    const urlBuilderEnabled = ref(false)
    const appsLoading = ref(false)
    const user = userObj()

    if (levels.indexOf(user.type) === -1) {
      statProp.value = 'appsWithEmbedWithHidden'
    } else {
      statProp.value = 'appsWithEmbedCount'
    }

    /**
     * @type {String}
     * @description A list of the publish domains for an account
     */
    const domains = ref(
      props.activeAgency.settings.publishDomain
        .map((d) => protocol + d.trim())
        .join(','),
    )

    /**
     * @type {String}
     * @description the property to sort the apps data on, this is set up
     * as a computed property so we can trigger the fetchApps method on
     * change
     */
    const computedSortProp = computed({
      get() {
        return sortProp.value
      },
      async set(value) {
        sortProp.value = value
        await fetchApps()
      },
    })

    /**
     * @type {String}
     * @description The computed app type for the component
     */
    const computedType = computed(() => {
      return capitalizeFirstLetter(props.appType)
    })

    /**
     * @type {String}
     * @description The computed title for the component
     */
    const pageTitle = computed(() => {
      return `${props.campaignId ? '' : 'All '}${computedType.value}`
    })

    /**
     * @type {String}
     * @description The computed entity branding for leads
     */
    const leadsBranding = computed(() => {
      return entityBranding('leads.plural', 'Leads')
    })

    /**
     * @type {String}
     * @description The computed button text for the all leads button
     */
    const leadsButtonText = computed(() => {
      return `View ${props.campaignId ? 'Project' : 'All'} ${
        leadsBranding.value
      }`
    })

    /**
     * @type {String}
     * @description Computed placeholder for the search input bases on the
     * computed type
     */
    const inputPlaceholder = computed(() => {
      return `Search ${computedType.value}`
    })

    /**
     * @type {Object}
     * @description Forms part of the request for fetching apps based on the
     * computed app type, this forms the app tyoe for the request.
     */
    const typeRequest = computed(() => {
      return props.appType === 'forms'
        ? 'embed'
        : JSON.stringify({ $in: ['promo', 'voucher', 'competition'] })
    })

    /**
     * @type {Array}
     * @description Options for the sort dropdown menu
     */
    const sortFieldOptions = ref([
      { value: 'createdOn', label: 'Created Date' },
      { value: 'updatedOn', label: 'Edited Date' },
      { value: 'startDate', label: 'Start Date' },
      { value: 'endDate', label: 'End Date' },
    ])

    /**
     * @type {Object}
     * @description Forms part of the request for fetching apps. this forms
     * the search part of the query
     */
    const searchOptions = computed(() => {
      const result = {}
      if (searchValue.value.length > 0) {
        const searchLower = searchValue.value.toLowerCase()
        result.titleLower = JSON.stringify({ $regex: searchLower })
      }
      return result
    })

    /**
     * @type {Object}
     * @description Forms the sort options part of the request for fetching apps
     */
    const sortOptions = computed(() => {
      return {
        sort: `${sortDirection.value === 'desc' ? '-' : ''}${
          sortProp.value.value
        }`,
      }
    })

    /**
     * @type {Object}
     * @description Forms the status part of the request for fetching apps
     */
    const statusOptions = computed(() => {
      const res = {}
      const type = activePublishedStatus.value

      if (type === 'hidden') {
        res.hidden = true
      }

      if (['published', 'draft'].indexOf(type) !== -1) {
        res.publishedStatus = type !== 'draft' ? true : false
        res.hidden = false
      }

      return res
    })

    /**
     * @type {Object}
     * @description Forms the full request object for fetching apps
     */
    const appsRequest = computed(() => {
      const params = {
        ...searchOptions.value,
        ...sortOptions.value,
        ...statusOptions.value,
        account: props.activeAccount._id,
        agency: props.activeAgency._id,
        per_page: 24,
        editable: JSON.stringify({ $ne: false }),
        type: typeRequest.value,
      }

      if (currentPage.value > 0) {
        params.page = currentPage.value
      }

      params.campaignFilter = JSON.stringify(
        props.campaignSidebarStore.activeFilter(),
      )

      if (levels.indexOf(user.type) !== -1) {
        params.hidden = false
      }

      return {
        params,
      }
    })

    /**
     * @type {Object}
     * @description Forms the link object for linking to the all leads section
     */
    const allLeadsLink = computed(() => {
      const link = {}
      if (props.campaignId) {
        link.name = 'campaignLeads'
        link.params = {
          campaignId: props.campaignId,
        }
      } else {
        link.name = 'allLeads'
      }
      return link
    })

    /**
     * @type {Object}
     * @description Forms the link object for creating a new app
     */
    const newAppLink = computed(() => {
      const params = {
        campaignId: props.campaignId,
        preCampaignId: props.campaignId,
      }
      if (props.appType === 'pages') {
        return { name: 'appNew', params: { ...params, typeSlug: 'promo' } }
      } else {
        return { name: 'formNew', params }
      }
    })

    /**
     * @type {Function}
     * @description Sets the view type for the "pages" part of the system,
     */
    const setView = (view) => {
      currentView.value = view
      props.setState('campaigns.view', view)
    }

    /**
     * @type {AsyncFunction}
     * @description resets the search value and fetches the apps data
     */
    const resetSearch = async () => {
      currentPage.value = 1
      searchValue.value = ''
      await fetchApps()
    }

    /**
     * @type {AsyncFunction}
     * @param {String} type
     * @description Sets the activePublishedStatus property and fetches apps
     * data
     */
    const setStateFilter = async (type) => {
      currentPage.value = 1
      activePublishedStatus.value =
        activePublishedStatus.value === type ? '' : type

      await fetchApps()
    }

    /**
     * @type {Function}
     * @param {String|Array} folder
     * @description Called when the sidebar folders change, this will also fetch
     * the apps data
     */
    const onFolderChange = (folder) => {
      if (typeof props.onCampaignChange === 'function') {
        props.onCampaignChange(folder)
      }

      router.push(
        typeof folder === 'string' && folder.length > 0
          ? 'appsListCampaign'
          : 'appsList',
        {
          campaignId: folder,
          cat: props.appType,
          type: 'all',
        },
      )
    }

    /**
     * @type {AsyncFunction}
     * @description Fetches the apps + stats from the API and returns an
     * object via a promise
     */
    const getApps = async () => {
      let appsData = []
      let totalNum = 0

      try {
        const { data } = await request.get('/apps', appsRequest.value)
        appsData = data.data
        totalNum = data.meta.pagination.total
        paging.value = data.meta.pagination
      } catch (ex) {
        console.log(ex)
      }

      return {
        appsData,
        totalNum,
      }
    }

    /**
     * @type {AsyncFunction}
     * @description Loads more apps into the component
     */
    const getMore = async () => {
      appsLoading.value = true
      currentPage.value += 1
      const { appsData, totalNum } = await getApps()
      apps.value = apps.value.concat(appsData)
      total.value = totalNum
      appsLoading.value = false
    }

    /**
     * @type {AsyncFunction}
     * @description Resets the page number before fetching the apps,
     * the search params are already baked into the fetchApps method
     * so we don't have to do anything with that here.
     */
    const searchApps = async () => {
      currentPage.value = 1
      await fetchApps()
    }

    /**
     * @type {AsyncFunction}
     * @description Fetches the apps data from the API, this will also try to
     * fetch the stats and map them onto the corresponding apps object.
     */
    const fetchApps = async () => {
      loading.value = true
      const { appsData, totalNum } = await getApps()
      apps.value = appsData
      total.value = totalNum
      loading.value = false
      fetchStats()
    }

    /**
     * @type {AsyncFunction}
     * @description Fetches the stats for the requested apps
     */
    const fetchStats = async () => {
      const fn = debounce(async () => {
        let stats = []
        try {
          const { data } = await request.post('/stats', {
            account: props.activeAccount._id,
            agency: props.activeAgency._id,
            type: 'appStats',
            ids: apps.value.map((i) => i._id),
          })
          stats = data.data
          apps.value = mapStats(apps.value, stats, ['leadCount'])
        } catch (ex) {
          console.log(ex)
        }
        return stats
      }, 500)

      fn()
    }

    /**
     * @type {AsyncFunction}
     * @description Allows the toggling of the sortDirection property, this will also
     * trigger the `fetchApps` method
     */
    const toggleSortDirection = async () => {
      sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
      await fetchApps()
    }

    /**
     * @param {Object} app - The app object to delete from the database
     * @returns {Promise}
     * @description Conforms if the user wants to delete an app before removing
     * it from the database
     */
    const deleteApp = async (app) => {
      const type = props.appType === 'pages' ? 'Page' : 'Form'

      DialogProgrammatic.prompt({
        title: `Deleting ${type}`,
        message: `
          <p>
            Permanently delete your ${type}, which will be unpublished from all locations.
            You will NOT lose any Leads generated by this ${type}.
          </p>
          <small><strong>Please type DELETE to confirm</strong></small>
        `,
        inputAttrs: {
          pattern: 'DELETE',
        },
        trapFocus: true,
        type: 'danger',
        hasIcon: true,
        size: 'small',
        confirmText: `Yes, Delete ${type}`,
        cancelText: `No, Keep ${type}`,
        onConfirm: () => onConfirmDelete(app, type),
      })
    }

    /**
     * @param {Object} app
     * @returns {Promise}
     * @description Triggers the deletion of an app once the user has
     * confirmed they want it removed
     */
    const onConfirmDelete = async (app, type) => {
      try {
        await request.delete(`/apps/${app._id}`)
        const idx = apps.value.findIndex((i) => i._id === app._id)
        if (idx >= 0) {
          apps.value.splice(idx, 1)
        }
        ToastProgrammatic.open({
          message: `Success your ${type} has been deleted.`,
          type: 'success',
        })
      } catch (ex) {
        ToastProgrammatic.open({
          message: `Failed to delete ${type}.`,
          type: 'danger',
        })
      }
    }

    /**
     * @param {Event} e - the click event of the clone modal button
     * @param {*} clonedApp - the app that has been cloned
     * @description This will trigger a route change to an app editor based on
     * the app type that is given
     */
    const goToEditor = (e, clonedApp) => {
      let location
      let params

      if (clonedApp.layoutBuilder) {
        location = 'campaigns.editor.edit'
        params = {
          editAppId: clonedApp._id,
        }
      } else {
        if (clonedApp.type === 'embed') {
          location = 'campaigns.embedEditor.edit'
        } else {
          location = 'campaigns.editor.edit'
        }

        params = {
          editAppId: clonedApp._id,
          agencyId: props.activeAgency._id,
          accountSlug: props.activeAccount.slug,
        }
      }

      router.push(location, params)
    }

    /**
     * @param {Object} app
     * @returns {AsyncFunction}
     * @description Takes an app object, and returns an async function
     * that will be used as the callback for the cloneAppModal
     */
    const cloneAppCallback = (appType) => async (e, data) => {
      try {
        const { data: res } = await request.post('/apps/clone', {
          from: data.originalAppDetails,
          postData: data.app,
        })

        ToastProgrammatic.open({
          message: `Success your ${appType} has been cloned.`,
          type: 'success',
        })

        if (data.app.account === data.originalAppDetails.account) {
          goToEditor(e, res.data)
        }
      } catch (ex) {
        ToastProgrammatic.open({
          message: `Failed to clone ${appType}.`,
          type: 'danger',
        })
      }
    }

    /**
     * @param {Object} app
     * @param {String} actionType
     * @description Triggers the clone/move app modal window
     */
    const cloneAppModal = (app, actionType) => {
      const type = props.appType === 'pages' ? 'Page' : 'Form'
      const title = capitalizeFirstLetter(actionType)
      const originalAppDetails = cloneObject(app)

      ModalProgrammatic.open({
        component: CloneMoveModal,
        outerScroll: true,
        submitForm: cloneAppCallback(type),
        size: 'large',
        props: {
          title: `${title} ${type} - ${app.title}`,
          modelValue: {
            originalAppDetails,
            app,
          },
          modalType: actionType,
          appType: type,
        },
      })
    }

    /**
     * @param {Object} app - The app to create a url for
     * @description Opens the create url modal window for the given app
     */
    const createUrlModal = (app) => {
      ModalProgrammatic.open({
        component: AppUrlBuilder,
        outerScroll: true,
        size: 'large',
        props: {
          modelValue: app,
        },
      })
    }

    /**
     * @returns {Promise}
     * @description Returns a promise to wait for the campaign sidebar to be ready before
     * we try to load the apps on most accounts this is pretty fast. however on harrisos
     * test account it can take a very long time due to the number of campaigns that he
     * has.
     *
     * So I am making it wait for up to 10 seconds (interval of 10ms * 1000 repititions).
     * On most accounts this will be done in just a couple of intervals.
     */
    const waitForSidebar = () => {
      return new Promise((resolve, reject) => {
        // If we do not have a campaignId then just resolve with an empty array
        // if (!props.campaignId) return resolve([])

        // Else we do have a campaign id, so now we need to wait untill the campaign
        // sidebar is ready and we have an array of campaign ID's that we can use in
        // the query to fetch the apps.
        setIntervalX(
          () => {
            try {
              if (props.campaignSidebarStore.isReady()) {
                resolve(props.campaignSidebarStore.activeCampaignIds())
                return true
              }
            } catch (ex) {
              reject(ex)
            }
          },
          10, // interval
          1000, // repititions
        )
      })
    }

    onMounted(async () => {
      // Try to set the view state, else ignore it
      try {
        if (props.appType === 'pages') {
          currentView.value = props.getState('campaigns.view')
        }
      } catch (ex) {
        /* istanbul ignore next */
      }
      const check = await isAllowed('urlBuilder')
      urlBuilderEnabled.value = check.access
      await waitForSidebar()
      await fetchApps()
    })

    return {
      activePublishedStatus,
      computedType,
      leadsBranding,
      searchValue,
      statProp,
      levels,
      setView,
      resetSearch,
      fetchApps,
      apps,
      total,
      currentPage,
      setStateFilter,
      onFolderChange,
      inputPlaceholder,
      currentView,
      loading,
      appsLoading,
      allLeadsLink,
      newAppLink,
      domains,
      leadsButtonText,
      pageTitle,
      getMore,
      sortFieldOptions,
      toggleSortDirection,
      sortDirection,
      sortProp,
      computedSortProp,
      deleteApp,
      cloneAppModal,
      createUrlModal,
      display,
      searchApps,
      urlBuilderEnabled,
    }
  },
})
</script>

<style lang="scss"></style>
